import * as THREE from 'three';
import { DRACOLoader, GLTFLoader, OrbitControls, RGBELoader } from 'three/examples/jsm/Addons.js';
import { RoundedBoxGeometry } from 'three/examples/jsm/Addons.js';
import { mergeVertices } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import GUI from 'lil-gui';
import CustomShaderMaterial from 'three-custom-shader-material/vanilla';

import wobbleVertexShader from './shaders/wobble/vertex.glsl';
import wobbleFragmentShader from './shaders/wobble/fragment.glsl';

/**
 * Debug
 */
// const gui = new GUI({ width: 400 });
const debugObject = {};

/**
 * Base
 */

// Loaders
const rgbeLoader = new RGBELoader();
const dracoLoader = new DRACOLoader();
dracoLoader.setDecoderPath('./draco/');
const gltfLoader = new GLTFLoader();
gltfLoader.setDRACOLoader(dracoLoader);

// Sizes
const sizes = {
	width: window.innerWidth,
	height: window.innerHeight,
	pixelRatio: Math.min(window.devicePixelRatio, 2),
};

window.addEventListener('resize', () => {
	// Sizes
	sizes.width = window.innerWidth;
	sizes.height = window.innerHeight;
	sizes.pixelRatio = Math.min(window.devicePixelRatio, 2);

	// Camera
	camera.aspect = sizes.width / sizes.height;
	camera.updateProjectionMatrix();

	// Renderer
	renderer.setSize(sizes.width, sizes.height);
	renderer.setPixelRatio(sizes.pixelRatio);
});

// Canvas
const canvas = document.querySelector('canvas.webgl');

// Scene
const scene = new THREE.Scene();
// rgbeLoader.load('./urban_alley_01_1k.hdr', (environmentMap) => {
// rgbeLoader.load('./studio_kominka_01_2k.hdr', (environmentMap) => {
rgbeLoader.load('./wooden_studio_08_2k.hdr', (environmentMap) => {
	environmentMap.mapping = THREE.EquirectangularReflectionMapping;

	// scene.background = environmentMap;
	scene.environment = environmentMap;
});

/**
 * Lights
 */
const directionalLight = new THREE.DirectionalLight(0xffffff, 3);
directionalLight.castShadow = true;
directionalLight.shadow.mapSize.set(1024, 1024);
directionalLight.shadow.camera.far = 15;
directionalLight.shadow.normalBias = 0.05;
directionalLight.position.set(0.25, 2, -2.25);
scene.add(directionalLight);

/**
 * Camera
 */

// Perspective camera
const camera = new THREE.PerspectiveCamera(35, sizes.width / sizes.height, 0.1, 100);
camera.position.set(13, -3, -5);
// camera.position.set(-0.45, 13.9, -3.07);
scene.add(camera);

// Orbit controls
const controls = new OrbitControls(camera, canvas);
controls.enableDamping = true;

// const guiCamera = gui.addFolder('Camera');
// guiCamera.add(camera.position, 'x').listen();
// guiCamera.add(camera.position, 'y').listen();
// guiCamera.add(camera.position, 'z').listen();

/**
 * Renderer
 */
const renderer = new THREE.WebGLRenderer({
	canvas: canvas,
	antialias: true,
});
renderer.shadowMap.enabled = true;
renderer.shadowMap.type = THREE.PCFSoftShadowMap;
renderer.toneMapping = THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure = 1;
renderer.setSize(sizes.width, sizes.height);
renderer.setPixelRatio(sizes.pixelRatio);

/**
 * Wobble
 */

// Material
debugObject.colorA = 0x0000ff;
debugObject.colorB = 0xff0000;
debugObject.sheenColor = 0x3500c7;

const uniforms = {
	uTime: new THREE.Uniform(0),

	uPositionFrequency: new THREE.Uniform(0.5),
	uTimeFrequency: new THREE.Uniform(0.4),
	uStrength: new THREE.Uniform(0.3),

	uWarpPositionFrequency: new THREE.Uniform(0.38),
	uWarpTimeFrequency: new THREE.Uniform(0.12),
	uWarpStrength: new THREE.Uniform(1.7),

	uColorA: new THREE.Uniform(new THREE.Color(debugObject.colorA)),
	uColorB: new THREE.Uniform(new THREE.Color(debugObject.colorB)),
};
const material = new CustomShaderMaterial({
	// CSM
	baseMaterial: THREE.MeshPhysicalMaterial,
	vertexShader: wobbleVertexShader,
	fragmentShader: wobbleFragmentShader,
	uniforms: uniforms,

	// MeshPhysicalMaterial
	transparent: false,
	wireframe: false,
	// Base
	color: 0xffffff,
	metalness: 0,
	roughness: 0.5,
	// Transmission
	transmission: 1,
	dispersion: 1,
	ior: 1.5,
	thickness: 1.5,
	// Clearcoat
	clearcoat: 1,
	clearcoatRoughness: 0,
	// Irridescence
	iridescence: 1,
	iridescenceIOR: 1.8,
	// Sheen
	sheen: 1,
	sheenColor: debugObject.sheenColor,
	sheenRoughness: 1,
});

const depthMaterial = new CustomShaderMaterial({
	// CSM
	baseMaterial: THREE.MeshDepthMaterial,
	vertexShader: wobbleVertexShader,
	uniforms: uniforms,

	// MeshDepthMaterial
	depthPacking: THREE.RGBADepthPacking,
});

// Tweaks
// gui.add(uniforms.uPositionFrequency, 'value', 0, 2, 0.001).name('uPositionFrequency');
// gui.add(uniforms.uTimeFrequency, 'value', 0, 2, 0.001).name('uTimeFrequency');
// gui.add(uniforms.uStrength, 'value', 0, 2, 0.001).name('uStrength');

// gui.add(uniforms.uWarpPositionFrequency, 'value', 0, 2, 0.001).name('uWarpPositionFrequency');
// gui.add(uniforms.uWarpTimeFrequency, 'value', 0, 2, 0.001).name('uWarpTimeFrequency');
// gui.add(uniforms.uWarpStrength, 'value', 0, 2, 0.001).name('uWarpStrength');

// gui.addColor(debugObject, 'colorA').onChange(() => uniforms.uColorA.value.set(debugObject.colorA));
// gui.addColor(debugObject, 'colorB').onChange(() => uniforms.uColorB.value.set(debugObject.colorB));

// const guiPhysical = gui.addFolder('Mesh Physical Material');
// guiPhysical.add(material, 'metalness', 0, 1, 0.001);
// guiPhysical.add(material, 'roughness', 0, 1, 0.001);
// guiPhysical.add(material, 'transmission', 0, 1, 0.001);
// guiPhysical.add(material, 'dispersion', 0, 10, 0.001);
// guiPhysical.add(material, 'ior', 0, 10, 0.001);
// guiPhysical.add(material, 'thickness', 0, 10, 0.001);
// guiPhysical.add(material, 'clearcoat', 0, 1, 0.001);
// guiPhysical.add(material, 'clearcoatRoughness', 0, 1, 0.001);
// guiPhysical.add(material, 'iridescence', 0, 1, 0.001);
// guiPhysical.add(material, 'iridescenceIOR', 0, 10, 0.001);
// guiPhysical.add(material, 'sheen', 0, 1, 0.001);
// guiPhysical.add(material, 'sheenRoughness', 0, 1, 0.001);
// guiPhysical.addColor(debugObject, 'sheenColor').onChange(() => material.sheenColor.set(debugObject.sheenColor));

// Geometry
let geometry = new THREE.IcosahedronGeometry(2.5, 64);
// let geometry = new THREE.TorusGeometry(3, 1.2, 192, 192 * 4);
// let geometry = new THREE.TorusKnotGeometry(2, 0.8, 128 * 8, 128, 2, 3);
// let geometry = new THREE.BoxGeometry(5, 5, 5, 128, 128, 128);
// let geometry = new RoundedBoxGeometry(5, 5, 5, 32, 1);
geometry = mergeVertices(geometry);
geometry.computeTangents();

// Mesh
const wobble = new THREE.Mesh(geometry, material);
wobble.customDepthMaterial = depthMaterial;
wobble.receiveShadow = true;
wobble.castShadow = true;
scene.add(wobble);

// // Model
// gltfLoader.load('./suzanne.glb', (gltf) => {
// 	const wobble = gltf.scene.children[0];
// 	wobble.receiveShadow = true;
// 	wobble.castShadow = true;
// 	wobble.material = material;
// 	wobble.customDepthMaterial = depthMaterial;

// 	scene.add(wobble);
// });

/**
 * Plane
 */
// const plane = new THREE.Mesh(
// 	new THREE.PlaneGeometry(15, 15, 15),
// 	new THREE.MeshStandardMaterial(),
// );
// plane.receiveShadow = true;
// plane.rotation.y = Math.PI;
// plane.position.y = -5;
// plane.position.z = 5;
// scene.add(plane);

/**
 * Loop
 */
const clock = new THREE.Clock();

const tick = () => {
	// Clock
	const elapsedTime = clock.getElapsedTime();

	// Materials
	uniforms.uTime.value = elapsedTime;

	// Controls
	controls.update();

	// Renderer
	renderer.render(scene, camera);

	window.requestAnimationFrame(tick);
}

tick();
